// Import main styles - CRITICAL FOR PRODUCTION BUILD
import "./styles/main.css";

import { initPaddleGlobal } from "./utils/paddle-init";

initPaddleGlobal();

import { StrictMode } from "react";
import * as ReactDOM from "react-dom/client";
import { RouterProvider } from "react-router-dom";

import { EnhancedErrorBoundary } from "./components/enhanced-error-boundary";
import { router } from "./router";
import { tokenConfigService } from "./services/token/token-config.service";
import { suppressConsoleLogs } from "./utils/logger";
import { reportWebVitals } from "./utils/web-vitals";

// Suppress console logs but keep errors visible
suppressConsoleLogs();

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const root = ReactDOM.createRoot(document.querySelector("#root")!);

/**
 * Bridge gtag() calls to dataLayer for web-vitals and any legacy callers.
 * GTM is bootstrapped inline in index.html, so we only need a safe shim here.
 */
function ensureGtagBridge(): void {
  if (typeof window === "undefined") return;
  const win = window as Window & {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  };
  win.dataLayer = win.dataLayer || [];
  if (typeof win.gtag !== "function") {
    win.gtag = (...args: unknown[]) => {
      win.dataLayer?.push(args);
    };
  }
}

// Initialize app - wait for token config before rendering
// This ensures getValueSync() returns database values instead of hardcoded defaults
async function initializeApp() {
  try {
    // Wait for token configuration to load (with 3s timeout to not block app)
    await Promise.race([
      tokenConfigService.preload(),
      new Promise((resolve) => setTimeout(resolve, 3000)),
    ]);
    console.log("✅ App initialized with token config");
  } catch (error) {
    console.warn("⚠️ Token config preload failed, using defaults:", error);
  }

  root.render(
    <StrictMode>
      <EnhancedErrorBoundary>
        <RouterProvider router={router} />
      </EnhancedErrorBoundary>
    </StrictMode>,
  );

  ensureGtagBridge();
  // Report Web Vitals for performance monitoring
  reportWebVitals();
}

initializeApp();

